import { useEffect, useState } from 'react';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Bar,
  Button,
  Card,
  CardHeader,
  Empty,
  ErrorState,
  Icon,
  Loading,
  StatTile,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/useApi';
import {
  money,
  moneyCompact,
  monthLabel,
  monthShort,
  number,
  percent,
  rate,
  shortDate,
} from '../lib/format';
import type {
  AgentResidual,
  CardBrandVolume,
  MerchantResidual,
  Paged,
  ResidualSummary,
} from '../lib/types';

const SERIES = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)'];

export function Reports() {
  const { user, scopeOf } = useAuth();
  const periods = useApi<string[]>('/reports/periods');
  const [period, setPeriod] = useState<string>('');
  const [sort, setSort] = useState<'residual' | 'volume' | 'rate'>('residual');
  const [exportError, setExportError] = useState<string | null>(null);

  // Default to the newest period as soon as the list arrives.
  useEffect(() => {
    if (!period && periods.data && periods.data.length > 0) setPeriod(periods.data[0]);
  }, [periods.data, period]);

  const summary = useApi<ResidualSummary>(
    period ? `/reports/residuals?period=${period}` : null,
  );
  const byAgent = useApi<AgentResidual[]>(
    period ? `/reports/residuals/by-agent?period=${period}` : null,
  );
  const byBrand = useApi<CardBrandVolume[]>(
    period ? `/reports/volume/by-card-brand?period=${period}` : null,
  );
  const byMerchant = useApi<Paged<MerchantResidual>>(
    period ? `/reports/residuals/by-merchant?period=${period}&sort=${sort}&limit=10` : null,
  );
  const trend = useApi<Array<{ period: string; residualCents: number }>>(
    '/reports/residuals/trend?months=12',
  );

  const exportScope = scopeOf('residual.export');
  const canExport = exportScope === 'all' || exportScope === 'down';

  async function download() {
    setExportError(null);
    try {
      const csv = await api.text(`/reports/residuals/export?period=${period}`);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `residuals-${period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(
        err instanceof ApiError ? err.message : 'The export did not run.',
      );
    }
  }

  const maxTrend = Math.max(1, ...(trend.data ?? []).map((t) => t.residualCents));
  const maxAgent = Math.max(1, ...(byAgent.data ?? []).map((a) => a.amountCents));

  return (
    <>
      <PageHeader
        breadcrumb={['Insight', 'Reports']}
        title="Residuals & volume"
        actions={
          <>
            <label className="field" style={{ minWidth: 190 }}>
              <span className="visually-hidden">Period</span>
              <select
                className="select"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                {(periods.data ?? []).map((p) => (
                  <option key={p} value={p}>
                    {monthLabel(p)}
                  </option>
                ))}
              </select>
            </label>
            {canExport ? (
              <Button
                variant="primary"
                icon={Icon.download({ size: 14 })}
                onClick={() => void download()}
              >
                Download statement
              </Button>
            ) : (
              <Button
                disabled
                title={
                  exportScope === 'ask'
                    ? 'Your node may request an export but not run one — ask your parent office.'
                    : 'Export is turned off for your node.'
                }
              >
                Export unavailable
              </Button>
            )}
          </>
        }
      />

      <ScopeBar>
        {user?.level === 'L4' ? (
          <>
            Your own line only. Splits paid upward to your office are not shown here.
          </>
        ) : (
          <>
            Figures roll up through <strong>{user?.nodeName}</strong>. An L4 agent sees
            only their own line; splits paid upward are not shown to them.
          </>
        )}
      </ScopeBar>

      <PageBody>
        {periods.error && <ErrorState error={periods.error} onRetry={periods.reload} />}
        {periods.data && periods.data.length === 0 && (
          <Empty
            title="No residual data yet"
            detail="Residuals appear once a period has been paid."
          />
        )}

        {exportError && (
          <div className="notice notice--warn" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{exportError}</span>
          </div>
        )}

        {summary.loading && <Loading />}
        {summary.error && <ErrorState error={summary.error} onRetry={summary.reload} />}

        {summary.data && (
          <div className="tilerow">
            <StatTile
              label={`Residual paid · ${monthLabel(summary.data.period).split(' ')[0]}`}
              value={money(summary.data.residualCents)}
              badge={
                summary.data.changePercent !== null
                  ? `${summary.data.changePercent >= 0 ? '▲' : '▼'} ${percent(Math.abs(summary.data.changePercent))}`
                  : undefined
              }
              badgeTone={(summary.data.changePercent ?? 0) >= 0 ? 'ok' : 'danger'}
              footnote={
                summary.data.paidAt ? `paid ${shortDate(summary.data.paidAt)}` : 'not yet paid'
              }
            />
            <StatTile
              label="Portfolio volume"
              value={moneyCompact(summary.data.volumeCents)}
              footnote="Across every merchant in view"
            />
            <StatTile
              label="Active MIDs"
              value={number(summary.data.merchantCount)}
              footnote="Earning in this period"
            />
            <StatTile
              label="Average effective rate"
              value={rate(summary.data.averageEffectiveRate)}
              footnote="Volume-weighted"
            />
          </div>
        )}

        <div className="split-even">
          <Card>
            <CardHeader
              title="Your monthly residual"
              meta={
                trend.data && trend.data.length > 0
                  ? `${monthLabel(trend.data[0].period)} – ${monthLabel(trend.data[trend.data.length - 1].period)}`
                  : undefined
              }
            />
            <p className="card__meta" style={{ marginTop: -8, marginBottom: 14 }}>
              Net of splits paid upward, before equipment chargebacks.
            </p>
            {trend.loading && <Loading />}
            {trend.data && trend.data.length > 0 && (
              <div className="trend">
                {trend.data.map((t) => (
                  <div className="trend__col" key={t.period}>
                    <div
                      className={`trend__bar ${t.period === period ? '' : 'trend__bar--muted'}`}
                      style={{ height: `${(t.residualCents / maxTrend) * 100}%` }}
                      title={`${monthLabel(t.period)}: ${money(t.residualCents)}`}
                    />
                    <span className="trend__label">{monthShort(t.period)}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <CardHeader title="Volume by card brand" />
            <p className="card__meta" style={{ marginTop: -8, marginBottom: 14 }}>
              {period ? monthLabel(period) : ''}, every merchant in view.
            </p>
            {byBrand.loading && <Loading />}
            {byBrand.data && (
              <div className="barlist">
                {byBrand.data.map((b, i) => (
                  <div className="barlist__row" key={b.brand}>
                    <div className="barlist__head">
                      <span className="barlist__name">{b.brand}</span>
                      <span className="barlist__share">{percent(b.share)}</span>
                      <span className="barlist__value">{moneyCompact(b.volumeCents)}</span>
                    </div>
                    <Bar share={b.share} color={SERIES[i % SERIES.length]} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="split-2">
          <Card>
            <CardHeader
              title="Merchant detail"
              action={
                <select
                  className="select"
                  style={{ width: 190, height: 30, fontSize: 12.5 }}
                  value={sort}
                  onChange={(e) => setSort(e.target.value as typeof sort)}
                  aria-label="Sort merchant detail"
                >
                  <option value="residual">Residual, high to low</option>
                  <option value="volume">Volume, high to low</option>
                  <option value="rate">Effective rate</option>
                </select>
              }
            />
            {byMerchant.loading && <Loading />}
            {byMerchant.data && byMerchant.data.items.length === 0 && (
              <Empty title="No merchant earned in this period" />
            )}
            {byMerchant.data && byMerchant.data.items.length > 0 && (
              <>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Merchant</th>
                        <th>Agent</th>
                        <th className="num">Volume</th>
                        <th className="num">Eff. rate</th>
                        <th className="num">Your share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byMerchant.data.items.map((m) => (
                        <tr key={m.merchantId}>
                          <td>
                            <div className="cell-primary">{m.merchantName}</div>
                            <div className="cell-sub">MID {m.mid}</div>
                          </td>
                          <td>{m.agentName}</td>
                          <td className="num">{money(m.volumeCents)}</td>
                          <td className="num">{rate(m.effectiveRate)}</td>
                          <td className="num">{money(m.yourShareCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="pager">
                  <span>
                    Showing {byMerchant.data.items.length} of {byMerchant.data.total}{' '}
                    merchants
                  </span>
                </div>
              </>
            )}
          </Card>

          <Card>
            <CardHeader title="Where the residual comes from" />
            <p className="card__meta" style={{ marginTop: -8, marginBottom: 14 }}>
              Your share, by the line that produced it.
            </p>
            {byAgent.loading && <Loading />}
            {byAgent.data && byAgent.data.length === 0 && <Empty title="Nothing earned" />}
            {byAgent.data && byAgent.data.length > 0 && (
              <div className="barlist">
                {byAgent.data.map((a, i) => (
                  <div className="barlist__row" key={a.agentUserId + String(i)}>
                    <div className="barlist__head">
                      <span className="barlist__name">{a.agentName}</span>
                      <span className="barlist__share">
                        {a.merchants} merchant{a.merchants === 1 ? '' : 's'}
                      </span>
                      <span className="barlist__value">{money(a.amountCents)}</span>
                    </div>
                    <Bar
                      share={(a.amountCents / maxAgent) * 100}
                      color={SERIES[i % SERIES.length]}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </PageBody>
    </>
  );
}
